import { html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Member } from "@/types";
import { formatDate, getInitials } from "@/utils";
import { closeIcon, refreshCwIcon, searchIcon } from "@/assets/icons";

export type MemberFilters = {
  roles: string[];
};

export type RoleOption = {
  value: string;
  label: string;
};

@customElement("sb-member-management-members-panel")
export class MemberManagementMembersPanel extends LitElement {
  @property({ attribute: false }) members: Member[] = [];
  @property({ type: Boolean, attribute: false }) membersLoading = false;
  @property({ type: String }) searchTerm = "";
  @property({ type: Object, attribute: false }) memberFilters: MemberFilters = {
    roles: [],
  };
  @property({ type: Array, attribute: false }) availableRoles: RoleOption[] =
    [];
  @property({ type: Number }) membersPage = 1;
  @property({ type: Number }) membersTotalPages = 0;
  @property({ type: Number }) membersTotal = 0;
  @property({ type: Number }) rowsPerPage = 5;

  @state() private _searchDebounceTimer: number | undefined;

  protected createRenderRoot() {
    return this;
  }

  private dispatchMembersSearchChange(searchTerm: string) {
    this.dispatchEvent(
      new CustomEvent("members-search-change", {
        detail: { searchTerm },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleMembersSearchChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;

    if (this._searchDebounceTimer) {
      window.clearTimeout(this._searchDebounceTimer);
    }

    this._searchDebounceTimer = window.setTimeout(() => {
      this._searchDebounceTimer = undefined;
      this.dispatchMembersSearchChange(this.searchTerm);
    }, 300);
  }

  private handleMembersPageChange(event: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent("members-page-change", {
        detail: { page: event.detail.page },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleRowsPerPageChange(event: Event) {
    const updatedValue = parseInt(
      (event.target as HTMLSelectElement).value,
      10,
    );
    this.dispatchEvent(
      new CustomEvent("members-rows-per-page-change", {
        detail: { rowsPerPage: updatedValue },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleRefresh() {
    this.dispatchEvent(
      new CustomEvent("members-refresh", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleRolesChange(event: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent("members-filter-change", {
        detail: { roles: event.detail.selected },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private clearFilters() {
    this.dispatchEvent(
      new CustomEvent("members-clear-filters", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private removeFilter(filterValue: string) {
    this.dispatchEvent(
      new CustomEvent("members-remove-filter", {
        detail: { filterValue },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private dispatchMemberAction(action: string, member: Member) {
    this.dispatchEvent(
      new CustomEvent(action, {
        detail: member,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private filterData(data: any[], searchTerm: string) {
    if (!searchTerm.trim()) return data;
    return data.filter((item) =>
      Object.values(item).some((value) =>
        typeof value === "string"
          ? value.toLowerCase().includes(searchTerm.toLowerCase())
          : false,
      ),
    );
  }

  private transformMemberWithName(member: Member) {
    return {
      ...member,
      name: member.user?.name || member.user?.email || "Unknown User",
      email: member.user?.email || "",
      avatar_url: member.user?.picture || "",
    };
  }

  private getSelectedFiltersDisplay() {
    const selectedFilters: Array<{
      name: string;
      value: string;
      label: string;
    }> = [];

    if (this.memberFilters.roles?.length) {
      this.memberFilters.roles.forEach((roleValue: string) => {
        const roleLabel =
          this.availableRoles.find((r) => r.value === roleValue)?.label ||
          roleValue;
        selectedFilters.push({
          name: "roles",
          value: roleValue,
          label: roleLabel,
        });
      });
    }

    return selectedFilters;
  }

  private getMemberTableConfig(loading: boolean) {
    const cellRenderers = {
      name: (_: any, row: Member) => {
        if (loading) {
          return html`
            <div class="sb-flex sb-items-center sb-gap-2">
              <rtg-skeleton
                class="sb-h-10 sb-w-10 sb-rounded-full"
              ></rtg-skeleton>
              <div class="sb-flex sb-flex-col sb-gap-1">
                <rtg-skeleton class="sb-h-4 sb-w-24"></rtg-skeleton>
                <rtg-skeleton class="sb-h-3 sb-w-32"></rtg-skeleton>
              </div>
            </div>
          `;
        }

        const displayName = row.user?.name || row.user?.email || "Unknown User";
        return html`
          <div class="sb-flex sb-items-center sb-gap-2">
            <div>
              <rtg-avatar size="lg">
                <rtg-avatar-image
                  src=${row.user?.picture || ""}
                  alt="avatar"
                ></rtg-avatar-image>
                <rtg-avatar-fallback
                  >${getInitials(displayName)}</rtg-avatar-fallback
                >
              </rtg-avatar>
            </div>
            <div class="sb-flex sb-flex-col">
              <rtg-typography-table part="name"
                >${displayName}</rtg-typography-table
              >
              <rtg-typography-muted part="email"
                >${row.user?.email || ""}</rtg-typography-muted
              >
            </div>
          </div>
        `;
      },
      joined_at: (value: any) => {
        if (loading) {
          return html`
            <div class="sb-flex sb-flex-col sb-gap-1">
              <rtg-skeleton class="sb-h-4 sb-w-16"></rtg-skeleton>
              <rtg-skeleton class="sb-h-3 sb-w-12"></rtg-skeleton>
            </div>
          `;
        }
        const { date, time } = formatDate(value);
        return html`
          <div class="sb-flex sb-flex-col">
            <rtg-typography-table part="joined_at"
              >${date}</rtg-typography-table
            >
            <rtg-typography-muted part="joined_time"
              >${time}</rtg-typography-muted
            >
          </div>
        `;
      },
      roles: (value: { key: string; name: string }[]) => {
        if (loading) {
          return html`
            <div class="sb-flex sb-gap-1">
              <rtg-skeleton class="sb-h-5 sb-w-12 sb-rounded"></rtg-skeleton>
              <rtg-skeleton class="sb-h-5 sb-w-16 sb-rounded"></rtg-skeleton>
            </div>
          `;
        }
        return html`
          <div class="sb-flex sb-gap-1 sb-flex-wrap">
            ${value.map(
              (role) => html`
                <rtg-badge variant="outline">${role.name}</rtg-badge>
              `,
            )}
          </div>
        `;
      },
      organization: (_: any, row: Member) => {
        if (loading) {
          return html` <rtg-skeleton class="sb-h-4 sb-w-20"></rtg-skeleton> `;
        }
        return html`
          <rtg-typography-table part="organization"
            >${row.organization_name ||
            row.organization_slug}</rtg-typography-table
          >
        `;
      },
    };

    const headerRenderers = Object.fromEntries(
      ["name", "roles", "joined_at"].map((key) => [
        key,
        () => {
          const labels: Record<string, string> = {
            name: "Name",
            roles: "Roles",
            joined_at: "Joined",
          };
          return html` <span>${labels[key] || key}</span> `;
        },
      ]),
    );

    const rowActions = loading
      ? []
      : [
          {
            label: "Manage roles",
            action: (row: Member) =>
              this.dispatchMemberAction("manage-member-roles", row),
          },
          {
            label: "Suspend user",
            action: (row: Member) =>
              this.dispatchMemberAction("suspend-member", row),
          },
          {
            render: () => html`<span part="danger-action">Remove user</span>`,
            action: (row: Member) =>
              this.dispatchMemberAction("remove-member", row),
          },
        ];

    return {
      cellRenderers,
      headerRenderers,
      rowActions,
      sortableColumns: loading ? [] : ["name", "joined_at"],
    };
  }

  private renderPageLengthSelection() {
    const startIndex =
      this.membersTotal === 0
        ? 0
        : (this.membersPage - 1) * this.rowsPerPage + 1;
    const endIndex = Math.min(
      this.membersPage * this.rowsPerPage,
      this.membersTotal,
    );

    return html`
      <div class="sb-flex sb-items-center sb-gap-1">
        <rtg-typography-muted>Rows per page</rtg-typography-muted>
        <rtg-select
          .value=${`${this.rowsPerPage}`}
          @change=${this.handleRowsPerPageChange}
        >
          <rtg-select-trigger data-testid="trigger" class="sb-w-full">
            <rtg-select-value></rtg-select-value>
          </rtg-select-trigger>
          <rtg-select-group slot="content">
            <rtg-select-item tabindex="0" value="5">5</rtg-select-item>
            <rtg-select-item tabindex="1" value="10">10</rtg-select-item>
            <rtg-select-item tabindex="2" value="25">25</rtg-select-item>
          </rtg-select-group>
        </rtg-select>
        <div class="sb-ml-1"></div>
        <rtg-separator orientation="vertical"></rtg-separator>
        <div class="sb-mr-1"></div>
        <rtg-typography-muted>
          ${this.membersTotal === 0
            ? "Showing 0 items"
            : `Showing ${startIndex} to ${endIndex} items of ${this.membersTotal}`}
        </rtg-typography-muted>
      </div>
    `;
  }

  render() {
    const memberConfig = this.getMemberTableConfig(this.membersLoading);
    const membersStub = Array.from({ length: this.rowsPerPage }, (_, i) => ({
      id: `stub-member-${i}`,
      name: "",
      email: "",
      roles: [],
      joined_at: 0,
      last_active_at: 0,
      avatar_url: "",
    }));

    const membersData = this.membersLoading
      ? membersStub
      : this.members.map((member) => this.transformMemberWithName(member));

    return html`
      <div class="sb-flex sb-justify-between">
        <div class="sb-mb-4 sb-flex sb-flex-wrap sb-items-center sb-gap-4">
          <rtg-input-group class="sb-flex-shrink-0 !sb-w-auto">
            <rtg-input-group-input
              placeholder="Search members..."
              .value=${this.searchTerm}
              @input=${this.handleMembersSearchChange}
            ></rtg-input-group-input>
            <rtg-input-group-addon>${searchIcon()}</rtg-input-group-addon>
          </rtg-input-group>
          <div class="sb-flex sb-items-center sb-gap-2">
            <sb-multi-select-filter
              label="Roles"
              placeholder="All roles"
              .options=${this.availableRoles}
              .selected=${this.memberFilters.roles}
              @change=${this.handleRolesChange}
            ></sb-multi-select-filter>
            ${this.memberFilters.roles?.length
              ? html`
                  <rtg-button
                    @click=${this.clearFilters}
                    variant="ghost"
                    size="sm"
                  >
                    ${closeIcon()} Clear All filters
                  </rtg-button>
                `
              : nothing}
          </div>
        </div>
        <rtg-button @click=${this.handleRefresh} variant="outline" size="icon">
          ${refreshCwIcon()}
        </rtg-button>
      </div>

      ${this.getSelectedFiltersDisplay().length
        ? html`
            <div class="sb-flex sb-flex-wrap sb-gap-2 sb-mb-4">
              ${this.getSelectedFiltersDisplay().map(
                (filter) => html`
                  <rtg-badge variant="secondary">
                    ${filter.label}
                    <span
                      class="sb-cursor-pointer sb-ml-1"
                      @click=${() => this.removeFilter(filter.value)}
                    >
                      ${closeIcon()}
                    </span>
                  </rtg-badge>
                `,
              )}
            </div>
          `
        : nothing}

      <rtg-data-table
        data-tab="members"
        .dataTable=${membersData}
        .cellRenderers=${memberConfig.cellRenderers}
        .headerRenderers=${memberConfig.headerRenderers}
        .rowActions=${memberConfig.rowActions}
        .pageSize=${membersData.length}
        .hasDefaultPagination=${false}
        .hasDefaultSearch=${false}
        .hasDefaultSelect=${false}
        .sortableColumns=${memberConfig.sortableColumns}
        .columnOrder=${["name", "roles", "joined_at", "last_active_at"]}
      ></rtg-data-table>

      ${!this.membersLoading
        ? html`
            <div class="sb-mt-4 sb-flex sb-justify-between">
              <div class="sb-flex sb-items-center sb-gap-1">
                ${this.renderPageLengthSelection()}
              </div>
              <div>
                <sb-pagination
                  .totalPages=${this.membersTotalPages}
                  .currentPage=${this.membersPage}
                  @page-change=${this.handleMembersPageChange}
                ></sb-pagination>
              </div>
            </div>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sb-member-management-members-panel": MemberManagementMembersPanel;
  }
}
